import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  LogLevel,
  type AccountInfo,
  type Configuration,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser';

/**
 * Entra ID sign-in from the browser, authorization code + PKCE, no Cognito.
 *
 * MSAL redirects to Entra and back; the API then validates the resulting
 * access token on every call. This is the whole authentication design, and it
 * is what lets the same API serve both the SPA and non-browser clients -- ALB
 * `authenticate-oidc` can do neither of those things (it cannot mint a session
 * for a non-browser caller, and it cannot redirect an SSE handshake).
 */

export interface AuthConfig {
  readonly tenantId: string;
  readonly spaClientId: string;
  readonly apiScope: string;
}

/** The API scope requested alongside the standard OIDC scopes. */
export function scopesFor(cfg: AuthConfig): string[] {
  return [cfg.apiScope];
}

export function buildMsalConfig(cfg: AuthConfig): Configuration {
  return {
    auth: {
      clientId: cfg.spaClientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      redirectUri: `${window.location.origin}/auth/callback`,
      postLogoutRedirectUri: `${window.location.origin}/signed-out`,
      // Keeps the user on the page that triggered the login rather than
      // dumping them at the dashboard after every token refresh.
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // sessionStorage, not localStorage: tokens die with the browser tab.
      // On a shared support workstation that difference is the control.
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
        piiLoggingEnabled: false,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) console.error(`[msal] ${message}`);
          else if (level === LogLevel.Warning) console.warn(`[msal] ${message}`);
        },
      },
    },
  };
}

let instance: PublicClientApplication | null = null;
let initialised: Promise<void> | null = null;

export function createMsalClient(cfg: AuthConfig): PublicClientApplication {
  instance ??= new PublicClientApplication(buildMsalConfig(cfg));
  return instance;
}

export function getMsalClient(): PublicClientApplication {
  if (!instance) throw new Error('MSAL client has not been created');
  return instance;
}

/**
 * MSAL v3 requires an explicit initialize() before any other call, and
 * handleRedirectPromise() must run exactly once on load to consume the code in
 * the URL fragment. Both are memoised because React StrictMode double-invokes
 * effects in development and a second handleRedirectPromise would find nothing
 * and clear the account.
 */
export async function initialiseMsal(cfg: AuthConfig): Promise<AccountInfo | null> {
  const client = createMsalClient(cfg);

  initialised ??= client.initialize();
  await initialised;

  const result = await client.handleRedirectPromise();
  if (result?.account) {
    client.setActiveAccount(result.account);
    return result.account;
  }

  const existing = client.getActiveAccount() ?? client.getAllAccounts()[0] ?? null;
  if (existing) client.setActiveAccount(existing);
  return existing;
}

export async function signIn(cfg: AuthConfig, loginHint?: string): Promise<void> {
  const request: RedirectRequest = {
    scopes: scopesFor(cfg),
    ...(loginHint ? { loginHint } : {}),
  };
  await getMsalClient().loginRedirect(request);
}

/**
 * Sign-out.
 *
 * Clearing the local cache alone leaves the Entra session intact, so the next
 * sign-in silently reauthenticates the same person -- which is wrong on a
 * shared workstation. logoutRedirect ends both.
 */
export async function signOut(): Promise<void> {
  const client = getMsalClient();
  const account = client.getActiveAccount();
  await client.logoutRedirect({
    account: account ?? undefined,
    postLogoutRedirectUri: `${window.location.origin}/signed-out`,
  });
}

/**
 * Returns a valid access token, refreshing silently when possible.
 *
 * Falls back to a full redirect only when Entra says interaction is genuinely
 * required, such as a Conditional Access policy demanding re-authentication or
 * MFA. Any other failure is surfaced rather than swallowed, because silently
 * redirecting on a transient network error produces an infinite login loop.
 */
export async function acquireToken(cfg: AuthConfig): Promise<string> {
  const client = getMsalClient();
  const account = client.getActiveAccount();

  if (!account) {
    await signIn(cfg);
    // loginRedirect navigates away; this never resolves in practice.
    throw new Error('Redirecting to sign in');
  }

  const request: SilentRequest = { account, scopes: scopesFor(cfg) };

  try {
    const result = await client.acquireTokenSilent(request);
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await client.acquireTokenRedirect(request);
      throw new Error('Redirecting to sign in');
    }
    throw err;
  }
}

export function activeAccount(): AccountInfo | null {
  return instance?.getActiveAccount() ?? null;
}

/** "Jiri Kosar" -> "JK". Falls back to the UPN when no name is present. */
export function initialsFor(account: { name?: string; username: string }): string {
  const source = account.name?.trim() || account.username;
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const second = parts.length > 1 ? (parts[1]?.[0] ?? '') : '';
  return (first + second).toUpperCase();
}
